import React from 'react';
import { useTranslation } from 'react-i18next';

import styles from './index.module.css';
import Cropper from 'react-easy-crop';
import { Slider } from '@material-ui/core';
import { Button as NucleusButton } from '@vivaahealth/design-system';
import { Button, Margin } from '../../../../components/Button';
import {
  LoadingSpinner,
  LoadingSpinnerSize,
} from '../../../../components/LoadingSpinner';
import { onKeyPress } from '../../../../utils/keypress';

interface Props {
  file: Blob;
  onSave: (picture: Blob) => void;
  instructions?: string;
  continueLabel?: string;
  rotateLabel?: string;
  useNucleusComponents?: boolean;
}

const DEFAULT_INSTRUCTIONS = 'Drag to reposition photo';
const DEFAULT_CONTINUE_LABEL = 'Save and continue';

const DEFAULT_ROTATE_LABEL = 'Rotate';

export const EditTool: React.FC<Props> = ({
  file,
  onSave,
  continueLabel = DEFAULT_CONTINUE_LABEL,
  instructions = DEFAULT_INSTRUCTIONS,
  rotateLabel = DEFAULT_ROTATE_LABEL,
  useNucleusComponents = false,
}) => {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = React.useState(false);
  const [crop, setCrop] = React.useState({ x: 0, y: 0 });
  const [zoom, setZoom] = React.useState(1);
  const [rotation, setRotation] = React.useState(0);
  const [image, setImage] = React.useState<string | null>(null);
  const [croppedZone, setCroppedZone] = React.useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });
  const [cropSize, setCropSize] = React.useState<{
    width: number;
    height: number;
  } | null>();

  const onZoomChange = (value: number) => {
    setZoom(value);
  };

  const onCropComplete = (
    croppedArea: { x: number; y: number; width: number; height: number },
    croppedAreaPixels: { x: number; y: number; width: number; height: number },
  ) => {
    setCroppedZone(croppedAreaPixels);
  };

  const getLoadedImage = async (
    base64Image: string,
  ): Promise<HTMLImageElement> => {
    return new Promise((resolve) => {
      const loadedImage = new Image();
      loadedImage.addEventListener('load', () => {
        resolve(loadedImage);
      });
      loadedImage.src = base64Image;
    });
  };

  const getResizedBase64 = async (image: HTMLImageElement): Promise<string> => {
    let percentage = 1;
    if (image.width > image.height) {
      percentage = 2000 / image.width;
    } else {
      percentage = 2000 / image.height;
    }
    const canvas = document.createElement('canvas');
    canvas.width = image.width * percentage;
    canvas.height = image.height * percentage;
    const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
    ctx.drawImage(
      image,
      0,
      0,
      image.width,
      image.height,
      0,
      0,
      canvas.width,
      canvas.height,
    );
    const base64 = canvas.toDataURL('image/jpeg');
    canvas.remove();
    return base64;
  };

  React.useEffect(() => {
    var reader = new FileReader();
    reader.onloadend = async () => {
      const base64 = reader.result;
      if (typeof base64 === 'string') {
        const image = await getLoadedImage(base64);
        if (image.width > 2000 || image.height > 2000) {
          const resizedBase64 = await getResizedBase64(image);
          setImage(resizedBase64);
        } else {
          setImage(base64);
        }
      }
    };
    reader.readAsDataURL(file);
  }, [file]);

  const cropImage = async () => {
    if (!image) return;
    const loadedImage = await getLoadedImage(image);
    const canvas = document.createElement('canvas');
    canvas.width = croppedZone.width;
    canvas.height = croppedZone.height;

    const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
    const maxSize = Math.max(loadedImage.width, loadedImage.height);
    const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));

    // set each dimensions to double largest dimension to allow for a safe area for the
    // image to rotate in without being clipped by canvas context
    canvas.width = safeArea;
    canvas.height = safeArea;

    // translate canvas context to a central location on image to allow rotating around the center.
    ctx.translate(safeArea / 2, safeArea / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.translate(-safeArea / 2, -safeArea / 2);

    // fill white background
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, safeArea, safeArea);

    // draw rotated image and store data.
    ctx.drawImage(
      loadedImage,
      safeArea / 2 - loadedImage.width * 0.5,
      safeArea / 2 - loadedImage.height * 0.5,
    );
    const data = ctx.getImageData(0, 0, safeArea, safeArea);

    // set canvas width to final desired crop size - this will clear existing context
    canvas.width = croppedZone.width;
    canvas.height = croppedZone.height;

    // fill white background
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, safeArea, safeArea);

    // paste generated rotate image with correct offsets for x,y crop values.
    ctx.putImageData(
      data,
      0 - safeArea / 2 + loadedImage.width * 0.5 - croppedZone.x,
      0 - safeArea / 2 + loadedImage.height * 0.5 - croppedZone.y,
      croppedZone.x < 0 ? croppedZone.x * -1 : 0,
      croppedZone.y < 0 ? croppedZone.x * -1 : 0,
      safeArea,
      safeArea,
    );
    canvas.toBlob((blob) => {
      canvas.remove();
      if (blob) {
        onSave(blob);
        setSubmitting(false);
      }
    }, 'image/jpeg');
  };

  const setFixedCropZone = (
    width: number,
    height: number,
    naturalWidth: number,
    naturalHeight: number,
  ) => {
    const aspect = 1.586;
    if (
      naturalWidth >= naturalHeight * aspect &&
      width > naturalHeight * aspect
    ) {
      setCropSize({
        width: naturalHeight * aspect,
        height: naturalHeight,
      });
      return;
    }

    if (width > naturalHeight * aspect) {
      setCropSize({
        width: naturalWidth,
        height: naturalWidth / aspect,
      });
      return;
    }

    if (width > height * aspect) {
      setCropSize({
        width: height * aspect,
        height: height,
      });
      return;
    }

    setCropSize({
      width: width,
      height: width / aspect,
    });
  };

  return (
    <>
      {!image && (
        <div className={styles.Loading}>
          <LoadingSpinner size={LoadingSpinnerSize.Small} />
        </div>
      )}
      {image && (
        <>
          <div
            className={styles.CropperContainer}
            tabIndex={0}
            role="grid"
            onKeyDown={(e) =>
              onKeyPress({
                e,
                onMoveLeft: () => setCrop({ x: crop.x + 1, y: crop.y }),
                onMoveUp: () => setCrop({ x: crop.x, y: crop.y + 1 }),
                onMoveRight: () => setCrop({ x: crop.x - 1, y: crop.y }),
                onMoveDown: () => setCrop({ x: crop.x, y: crop.y - 1 }),
              })
            }
          >
            <Cropper
              image={image}
              crop={crop}
              cropSize={cropSize || undefined}
              zoom={zoom}
              rotation={rotation}
              minZoom={0.6}
              maxZoom={4}
              aspect={1.586}
              restrictPosition={false}
              onCropChange={(value: { x: number; y: number }) => {
                setCrop(value);
              }}
              onCropComplete={onCropComplete}
              onZoomChange={onZoomChange}
              onRotationChange={(rotation) => {
                setRotation(rotation);
              }}
              onMediaLoaded={(media) => {
                setFixedCropZone(
                  media.width,
                  media.height,
                  media.naturalWidth,
                  media.naturalHeight,
                );
              }}
              showGrid={false}
              style={{
                mediaStyle: {
                  objectFit: 'cover',
                  maxHeight: 'initial',
                },
                cropAreaStyle: {
                  boxShadow: '0px 0px 0px 9998em rgba(0,0,0,0.7)',
                  borderRadius: '5px',
                  border: '2px solid white',
                },
              }}
            />
          </div>
          <div className={styles.Controls}>
            <div className={styles.SmallScreen}>
              {useNucleusComponents ? (
                <div className={styles.continueButtonWrapper}>
                  <NucleusButton
                    data-testid="save-cropped"
                    disabled={
                      !croppedZone.width || !croppedZone.height || submitting
                    }
                    fullWidth
                    onClick={() => {
                      setSubmitting(true);
                      cropImage();
                    }}
                  >
                    {t(continueLabel)}
                  </NucleusButton>
                </div>
              ) : (
                <Button
                  type="button"
                  disabled={
                    !croppedZone.width || !croppedZone.height || submitting
                  }
                  children={t(continueLabel)}
                  className={styles.SaveButton}
                  margin={Margin.Enum}
                  onClick={() => {
                    setSubmitting(true);
                    cropImage();
                  }}
                />
              )}
            </div>
            <div className={styles.Instructions}>{t(instructions)}</div>
            <div className={styles.SliderContainer}>
              <span>{t(`Zoom`)}</span>
              <Slider
                data-testid="zoom-slider"
                min={0.6}
                max={4}
                step={0.1}
                value={zoom}
                defaultValue={zoom}
                className={styles.Slider}
                onChange={(e, value: number | number[]) => {
                  if (!Array.isArray(value)) {
                    onZoomChange(value);
                  }
                }}
              />
            </div>
            <div className={styles.SliderContainer}>
              <span>{t(rotateLabel)}</span>
              <Slider
                data-testid="straighten-slider"
                min={-180}
                max={180}
                step={2}
                value={rotation}
                marks={[{ value: 0, label: null }]}
                defaultValue={rotation}
                className={styles.Slider}
                onChange={(e, value: number | number[]) => {
                  if (!Array.isArray(value)) {
                    setRotation(value);
                  }
                }}
              />
            </div>
            <div className={styles.LargeScreen}>
              {useNucleusComponents ? (
                <div className={styles.continueButtonWrapper}>
                  <NucleusButton
                    data-testid="save-cropped"
                    disabled={
                      !croppedZone.width || !croppedZone.height || submitting
                    }
                    fullWidth
                    onClick={() => {
                      setSubmitting(true);
                      cropImage();
                    }}
                  >
                    {t(continueLabel)}
                  </NucleusButton>
                </div>
              ) : (
                <Button
                  type="button"
                  disabled={
                    !croppedZone.width || !croppedZone.height || submitting
                  }
                  children={t(continueLabel)}
                  className={styles.SaveButton}
                  margin={Margin.Enum}
                  onClick={() => {
                    setSubmitting(true);
                    cropImage();
                  }}
                />
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};
